import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, timer, Subscription, combineLatest, of, Subject, EMPTY } from 'rxjs';
import { takeWhile, tap, switchMap, catchError, map, exhaustMap, takeUntil, finalize } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '@common/services/toast.service';
import { ToastMessageAction } from '@common/models';
import { environment } from '../../environments/environment';
import { ApiRequestService } from '@common/services';

export interface BackgroundJob {
  id: string;
  contextKey?: string;
  type: 'export' | 'bulk-sync' | 'batch-invoice' | 'import';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  name?: string;
  createdAt: Date;
  startTime: Date;
  downloadUrl?: string;
  error?: string;
  completedAt?: Date;
  updatedAt?: Date;
  format?:string;
  createdAtRaw?: string | null;
  updatedAtRaw?: string | null;
  processedRecords?: number;
  successCount?: number;
  errorCount?: number;
  totalRecords?: number;
  userID?: string | number;
  contextType?: 'client' | 'admin';
  contextId?: string | number;
  apiScope?: 'client' | 'admin';
}

export interface JobNotification {
  jobId: string;
  contextKey?: string;
  type: string;
  status: string;
  message: string;
  error?: string;
  name?: string;
  downloadUrl?: string;
  format?: string;
  timestamp: Date;
  updatedAt?: string | Date;
  expiresAt?: string | Date;
  isPinned?: boolean;
  downloadCount: number;
  userID?: string | number;
  contextType?: 'client' | 'admin';
  contextId?: string | number;
  apiScope?: 'client' | 'admin';
}

interface PollingController {
  subscription?: Subscription;
  stop$: Subject<void>;
  stopRequested: boolean;
  pendingRequests: number;
  completed: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundJobMonitorService {

  private activeJobsSubject = new BehaviorSubject<Map<string, BackgroundJob>>(new Map());
  private notificationsSubject = new BehaviorSubject<JobNotification[]>([]);
  private isMonitoringSubject = new BehaviorSubject<boolean>(false);
  
  public readonly activeJobs$ = this.activeJobsSubject.asObservable();
  public readonly notifications$ = this.notificationsSubject.asObservable();
  public readonly isMonitoring$ = this.isMonitoringSubject.asObservable();
  public readonly activeJobsCount$ = this.activeJobs$.pipe(
    map(jobsMap => jobsMap.size)
  );

  private monitoringSubscriptions = new Map<string, PollingController>();
  private readonly completionToastKeys = new Set<string>();
  private globalTimerSubscription?: Subscription;
  private readonly ACTIVE_JOBS_STORAGE_KEY = "background-job-monitor.active-jobs";


  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private apiRequest: ApiRequestService 
  ) {
    this.restoreActiveJobsFromStorage();
    this.startGlobalMonitoring();
  }

  ngOnDestroy(): void {
    this.stopAllMonitoring();
    if (this.globalTimerSubscription) {
      this.globalTimerSubscription.unsubscribe();
    }
  }

  /**
   * Start monitoring a new job
   */
  startMonitoringJob(
    jobId: string,
    type: BackgroundJob['type'],
    initialMessage: string = 'Job started',
    format?: string,
    name?: string,
    userID?: string | number,
    apiScope: 'client' | 'admin' = 'client'
  ): void {
    const contextType = apiScope;
    const contextId = this.resolveContextId(userID, apiScope);
    const contextKey = this.buildContextKey(jobId, contextType, contextId);
    const job: BackgroundJob = {
      id: jobId,
      contextKey,
      type,
      status: 'pending',
      progress: 0,
      message: initialMessage,
      name: name ?? this.getDefaultJobName(type),
      createdAt: new Date(),
      createdAtRaw: null,
      startTime: new Date(),
      updatedAt: new Date(),
      updatedAtRaw: null,
      format,
      userID,
      contextType,
      contextId: contextId ?? undefined,
      apiScope
    };

    this.addJob(job);
    this.startJobPolling(jobId, type, userID, apiScope, contextId);
  }

  /**
   * Stop monitoring a specific job
   */
  stopMonitoringJob(jobId: string): void {
    const jobsMap = new Map(this.activeJobsSubject.value);
    const jobKey = this.findJobKey(jobId);
    if (jobKey) {
      jobsMap.delete(jobKey);
      this.activeJobsSubject.next(jobsMap);
    }
    this.persistActiveJobs();

    if (jobKey) {
      this.stopPollingSubscription(jobKey);
    }
  }

  /**
   * Get all active jobs
   */
  getActiveJobs(): BackgroundJob[] {
    return Array.from(this.activeJobsSubject.value.values());
  }

  /**
   * Get jobs by type
   */
  getJobsByType(type: BackgroundJob['type']): BackgroundJob[] {
    return this.getActiveJobs().filter(job => job.type === type);
  }

  /**
   * Check if a specific job is active
   */
  isJobActive(jobId: string): boolean {
    return !!this.findJobKey(jobId);
  }

  /**
   * Get job status
   */
  getJobStatus(jobId: string): BackgroundJob | undefined {
    const key = this.findJobKey(jobId);
    return key ? this.activeJobsSubject.value.get(key) : undefined;
  }

  /**
   * Clear completed jobs
   */
  clearCompletedJobs(): void {
    const jobsMap = new Map(this.activeJobsSubject.value);
    const activeJobs = new Map<string, BackgroundJob>();
    
    for (const [id, job] of jobsMap) {
      if (job.status === 'pending' || job.status === 'processing') {
        activeJobs.set(id, job);
      }
    }
    
    this.activeJobsSubject.next(activeJobs);
    this.persistActiveJobs();
  }

  /**
   * Clear all notifications
   */
  clearNotifications(): void {
    this.notificationsSubject.next([]);
  }

  /**
   * Remove a specific notification
   */
  removeNotification(jobId: string): void {
    const notifications = this.notificationsSubject.value.filter(n => n.jobId !== jobId);
    this.notificationsSubject.next(notifications);
  }

  /**
   * Pin/unpin a notification to prevent auto-removal
   */
  toggleNotificationPin(jobId: string): void {
    const notifications = this.notificationsSubject.value.map(n => 
      n.jobId === jobId ? { ...n, isPinned: !n.isPinned } : n
    );
    this.notificationsSubject.next(notifications);
  }

  /**
   * Track download for a notification
   */
  trackDownload(jobId: string): void {
    const notifications = this.notificationsSubject.value.map(n => 
      n.jobId === jobId ? { ...n, downloadCount: n.downloadCount + 1 } : n
    );
    this.notificationsSubject.next(notifications);
  }

  refreshJobsFromServer(
    userIDOverride?: string | number | null,
    apiScope: 'client' | 'admin' = 'client'
  ): Observable<BackgroundJob[]> {
    const userID = this.resolveContextId(userIDOverride, apiScope);

    if (!userID) {
      return of([]);
    }

    const body = apiScope === 'admin'
      ? {
          payload: {
            adminID: userID,
          },
        }
      : {
          payload: {
            userID,
          },
        };

    const request$ = apiScope === 'admin'
      ? this.http.post<any>(`${environment.apiUrl}admin/common/admin_jobs`, body, { withCredentials: true })
      : this.apiRequest.apiRequest(
          'common/user_jobs/',
          'user_jobs.json',
          body,
          { cancelable: false }
        );

    return request$
      .pipe(
        map((response: any) => {
          if (response?.success === false) {
            return [];
          }

          const rawJobs =
            Array.isArray(response?.data?.jobs) ? response.data.jobs :
            Array.isArray(response?.data?.items) ? response.data.items :
            Array.isArray(response?.data) ? response.data :
            Array.isArray(response?.jobs) ? response.jobs :
            Array.isArray(response?.items) ? response.items :
            Array.isArray(response) ? response :
            [];

          const normalizedJobs = rawJobs
            .map((rawJob: any) => this.normalizeServerJob(rawJob, apiScope, userID))
            .filter((job: BackgroundJob | null): job is BackgroundJob => job !== null);

          this.syncJobsFromServer(rawJobs, apiScope, userID);
          this.syncNotificationsFromServer(rawJobs, normalizedJobs, apiScope, userID);

          return normalizedJobs;
        }),
        catchError(error => {
          console.error('Failed to refresh background jobs from server', error);
          return of([]);
        })
      );
  }

  private syncNotificationsFromServer(
    rawJobs: any[],
    normalizedJobs: BackgroundJob[],
    apiScope: 'client' | 'admin' = 'client',
    userIDOverride?: string | number | null
  ): void {
    const contextId = this.resolveContextId(userIDOverride, apiScope);
    const contextKeyPrefix = this.buildContextPrefix(apiScope, contextId);
    const rawJobMap = new Map<string, any>();
    rawJobs.forEach(rawJob => {
      const jobId = rawJob?.jobId || rawJob?.id;
      if (jobId) {
        const rawContextType = rawJob?.apiScope ?? apiScope;
        const rawContextId = this.resolveContextId(
          rawJob?.adminID ?? rawJob?.userID ?? rawJob?.ownerID ?? userIDOverride ?? null,
          rawContextType
        );
        rawJobMap.set(this.buildContextKey(String(jobId), rawContextType, rawContextId), rawJob);
        rawJobMap.set(String(jobId), rawJob);
      }
    });

    const existingNotifications = new Map<string, JobNotification>(
      this.notificationsSubject.value.map(notification => [
        notification.contextKey ?? this.buildContextKey(notification.jobId, notification.contextType ?? notification.apiScope ?? 'client', notification.contextId ?? null),
        notification
      ])
    );

    const nextNotifications: JobNotification[] = normalizedJobs.map(job => {
      const notificationKey = job.contextKey ?? this.buildContextKey(job.id, job.contextType ?? job.apiScope ?? 'client', job.contextId ?? null);
      const rawJob = rawJobMap.get(notificationKey) ?? rawJobMap.get(job.id);
      const timestamp =
        this.parseDate(rawJob?.timestamp) ??
        this.parseDate(rawJob?.completedAt || rawJob?.finishedAt || rawJob?.updatedAt) ??
        this.parseDate(rawJob?.startTime || rawJob?.startedAt || rawJob?.createdAt) ??
        job.completedAt ??
        job.createdAt ??
        job.startTime ??
        new Date();

      const existing = existingNotifications.get(notificationKey);

      return {
        jobId: job.id,
        contextKey: notificationKey,
        type: job.type,
        status: job.status,
        message: job.message,
        error:
          job.error ??
          rawJob?.error ??
          rawJob?.errorMessage ??
          rawJob?.statusMessage ??
          rawJob?.message,
        name: job.name,
        downloadUrl: job.downloadUrl ?? rawJob?.downloadUrl ?? rawJob?.reportUrl ?? rawJob?.fileUrl,
        format: job.format ?? rawJob?.format ?? rawJob?.fileFormat,
        userID: job.userID ?? rawJob?.userID ?? rawJob?.adminID,
        contextType: job.contextType ?? rawJob?.contextType ?? job.apiScope ?? 'client',
        contextId: job.contextId ?? rawJob?.adminID ?? rawJob?.userID,
        apiScope: job.apiScope ?? rawJob?.apiScope ?? 'client',
        timestamp,
        updatedAt: rawJob?.updatedAt ?? rawJob?.completedAt ?? rawJob?.finishedAt ?? rawJob?.timestamp,
        expiresAt: rawJob?.expiresAt ?? rawJob?.expirationDate ?? rawJob?.expires_on ?? rawJob?.expiresAtUtc,
        isPinned: existing?.isPinned ?? false,
        downloadCount: existing?.downloadCount ?? 0
      };
    });

    const mergedNotifications = this.notificationsSubject.value.filter(notification => {
      const key = notification.contextKey ?? this.buildContextKey(notification.jobId, notification.contextType ?? notification.apiScope ?? 'client', notification.contextId ?? null);
      return !key.startsWith(contextKeyPrefix);
    });

    mergedNotifications.push(...nextNotifications);
    this.notificationsSubject.next(mergedNotifications);
  }

  syncJobsFromServer(
    serverJobs: any[],
    apiScope: 'client' | 'admin' = 'client',
    userIDOverride?: string | number | null
  ): void {
    if (!Array.isArray(serverJobs)) {
      return;
    }
    const contextId = this.resolveContextId(userIDOverride, apiScope);
    const contextKeyPrefix = this.buildContextPrefix(apiScope, contextId);
    const nextJobsMap = new Map<string, BackgroundJob>();
    const pollingQueue: BackgroundJob[] = [];

    serverJobs.forEach(rawJob => {
      const normalizedJob = this.normalizeServerJob(rawJob, apiScope, userIDOverride);
      if (!normalizedJob) {
        return;
      }

      nextJobsMap.set(normalizedJob.contextKey ?? this.buildContextKey(normalizedJob.id, apiScope, contextId), normalizedJob);

      if (normalizedJob.status === 'pending' || normalizedJob.status === 'processing') {
        pollingQueue.push(normalizedJob);
      }
    });

    const mergedJobs = new Map<string, BackgroundJob>(this.activeJobsSubject.value);
    const existingJobIds = Array.from(mergedJobs.keys());
    existingJobIds.forEach(jobKey => {
      if (!jobKey.startsWith(contextKeyPrefix)) {
        return;
      }

      if (!nextJobsMap.has(jobKey)) {
        mergedJobs.delete(jobKey);
        this.stopPollingSubscription(jobKey);
      }
    });

    nextJobsMap.forEach((job, jobKey) => {
      mergedJobs.set(jobKey, job);
    });

    this.activeJobsSubject.next(mergedJobs);
    this.persistActiveJobs();

    pollingQueue.forEach(job => {
      const jobKey = job.contextKey ?? this.buildContextKey(job.id, job.apiScope ?? apiScope, job.contextId ?? contextId);
      if (!this.monitoringSubscriptions.has(jobKey)) {
        this.startJobPolling(job.id, job.type, job.userID, job.apiScope ?? apiScope, job.contextId ?? contextId);
      }
    });
  }

  private addJob(job: BackgroundJob): void {
    const jobsMap = new Map(this.activeJobsSubject.value);
    jobsMap.set(job.contextKey ?? this.buildContextKey(job.id, job.contextType ?? job.apiScope ?? 'client', job.contextId ?? null), job);
    this.activeJobsSubject.next(jobsMap);
    this.persistActiveJobs();
  }

  private updateJob(jobId: string, updates: Partial<BackgroundJob>): void {
    const jobsMap = new Map(this.activeJobsSubject.value);
    const jobKey = this.findJobKey(jobId);
    const job = jobKey ? jobsMap.get(jobKey) : undefined;
    if (job) {
      const parsedUpdatedAt =
        updates.updatedAt !== undefined
          ? (updates.updatedAt instanceof Date
              ? updates.updatedAt
              : this.parseDate(updates.updatedAt) ?? new Date())
          : new Date();

      const updatedJob = {
        ...job,
        ...updates,
        updatedAt: parsedUpdatedAt,
        updatedAtRaw: updates.updatedAtRaw !== undefined
          ? updates.updatedAtRaw
          : updates.updatedAt !== undefined
            ? (updates.updatedAt instanceof Date
                ? updates.updatedAt.toISOString()
                : String(updates.updatedAt))
            : job.updatedAtRaw
      };
      
      // Add completion timestamp when job is completed or failed
      if ((updatedJob.status === 'completed' || updatedJob.status === 'failed') && !updatedJob.completedAt) {
        updatedJob.completedAt = new Date();
      }
      
      jobsMap.set(jobKey as string, updatedJob);
      this.activeJobsSubject.next(jobsMap);
      this.persistActiveJobs();

      // Check if job is completed or failed
      if (updatedJob.status === 'completed' || updatedJob.status === 'failed') {
        this.handleJobCompletion(updatedJob);
      }
    }
  }

  private startJobPolling(
    jobId: string,
    type: BackgroundJob['type'],
    userIDOverride?: string | number,
    apiScope: 'client' | 'admin' = 'client',
    contextId?: string | number | null
  ): void {
    // Determine the API endpoint based on job type
    let endpoint: string;
    switch (type) {
      case 'import':
        endpoint = apiScope === 'admin' ? 'admin/common/report_job_status' : 'common/report_job_status';
        break;
      case 'export':
        endpoint = apiScope === 'admin' ? 'admin/common/report_job_status' : 'common/report_job_status';
        break;
      case 'bulk-sync':
        endpoint = apiScope === 'admin' ? 'admin/common/report_job_status' : 'common/report_job_status';
        break;
      case 'batch-invoice':
        endpoint = apiScope === 'admin' ? 'admin/common/report_job_status' : 'common/report_job_status';
        break;
      default:
        console.warn(`Unknown job type: ${type}`);
        return;
    }

    // Create a polling observable that calls the appropriate endpoint

    const userID = this.resolveContextId(userIDOverride ?? contextId ?? null, apiScope);

    if (!userID && apiScope !== 'admin') {
      return;
    }

    const jobKey = this.buildContextKey(jobId, apiScope, userID);

    const body = apiScope === 'admin'
      ? {
          payload: {
            jobId,
            adminID: userID
          }
        }
      : {
          payload: {
            userID,
            jobId
          }
        };


    const stop$ = new Subject<void>();
    const controller: PollingController = {
      stop$,
      stopRequested: false,
      pendingRequests: 0,
      completed: false
    };

    const tryFinalize = () => {
      if (controller.stopRequested && controller.pendingRequests === 0) {
        this.finalizePolling(jobId, controller);
      }
    };

    this.monitoringSubscriptions.set(jobKey, controller);

    const pollingObservable = timer(0, 2000).pipe(
      takeUntil(stop$),
      exhaustMap(() => {
        if (controller.stopRequested) {
          tryFinalize();
          return EMPTY;
        }

        controller.pendingRequests += 1;

        const request$ = apiScope === 'admin'
          ? this.http.post<any>(`${environment.apiUrl}${endpoint}`, body, { withCredentials: true })
          : this.apiRequest.apiRequest(
              endpoint,
              '',
              body,
              { cancelable: false }
            );

        return request$.pipe(
            catchError((error) => {
              console.warn('report_job_status polling error; will retry on next tick', error);
              return of({ status: 'processing', progress: 0, message: 'Re-trying...', error: error?.message });
            }),
            finalize(() => {
              controller.pendingRequests = Math.max(0, controller.pendingRequests - 1);
              tryFinalize();
            })
          );
      }),
      map((response: any) => this.extractJobStatusPayload(response)),
      tap((res: any) => {
        const processedRecords = this.coerceNumber(res?.processedRecords);
        const successCount = this.coerceNumber(res?.successCount);
        const errorCount = this.coerceNumber(res?.errorCount);
        const totalRecords = this.coerceNumber(res?.totalRecords);

        const update: Partial<BackgroundJob> = {
          status: this.normalizeJobStatus(res?.status),
          progress: this.coerceNumber(res.progress) ?? 0,
          message: res.message || res.statusMessage || 'Processing...',
          downloadUrl: res.reportUrl ?? res.downloadUrl ?? res.fileUrl,
          error: res.error ?? res.errorMessage ?? res.statusMessage,
          name: res.name || res.jobName || res.title || res.reportName,
          apiScope
        };

        if (processedRecords !== undefined) {
          update.processedRecords = processedRecords;
        }

        if (successCount !== undefined) {
          update.successCount = successCount;
        }

        if (errorCount !== undefined) {
          update.errorCount = errorCount;
        }

        if (totalRecords !== undefined) {
          update.totalRecords = totalRecords;
        }

        this.updateJob(jobKey, update);

        if (res?.status === 'completed' || res?.status === 'failed') {
          this.stopPollingSubscription(jobKey);
        }
      })
    );

    const subscription = pollingObservable.subscribe();
    controller.subscription = subscription;
  }

  private getDefaultJobName(type: BackgroundJob['type']): string {
    switch (type) {
      case 'import':
        return 'Import Job';
      case 'export':
        return 'Export Job';
      case 'bulk-sync':
        return 'Bulk Sync Job';
      case 'batch-invoice':
        return 'Batch Invoice Job';
      default:
        return 'Background Job';
    }
  }

  private handleJobCompletion(job: BackgroundJob): void {
    const completionKey = job.contextKey ?? this.buildContextKey(
      job.id,
      job.contextType ?? job.apiScope ?? 'client',
      job.contextId ?? null
    );

    // A polling response can be delivered more than once around shutdown, or
    // a restored job can be observed by more than one monitor. Completion is
    // a one-time event for the UI, so do not show another toast for the same
    // job context after it has already reached a terminal state.
    if (this.completionToastKeys.has(completionKey)) {
      return;
    }
    this.completionToastKeys.add(completionKey);

    // Refresh notifications from server to capture latest metadata
    this.refreshJobsFromServer(job.contextId ?? job.userID ?? null, job.apiScope ?? 'client').subscribe({
      error: error => {
        console.error('Failed to refresh user jobs after completion', error);
      }
    });

    // Show toast notification
    if (job.status === 'completed') {
      this.showCompletionToast(job);
    } else if (job.status === 'failed') {
      this.showErrorToast(job);
    }

    // Stop monitoring after a delay to allow user to see final status
    // But don't auto-remove if user has pinned the notification
    // setTimeout(() => {
    //   const currentNotification = this.notificationsSubject.value.find(n => n.jobId === job.id);
    //   if (currentNotification && !currentNotification.isPinned) {
    //     this.stopMonitoringJob(job.id);
    //   }
    // }, 60000); // 1 minute instead of 30 seconds
  }

  private showCompletionToast(job: BackgroundJob): void {
    let title = 'Job Completed';
    const originalMessage = typeof job.message === 'string' ? job.message.trim() : '';
    let message = originalMessage;
    let iconPath: string | undefined;
    let toastAction: ToastMessageAction | undefined;

    switch (job.type) {
      case 'export':
        title = 'Export Completed';
        message = 'Your data export is ready for download. You can download your data from the Job Status panel.';
        break;
      case 'bulk-sync':
        title = 'Sync Completed';
        message = 'Bulk synchronization has finished successfully.';
        break;
      case 'batch-invoice':
        title = 'Invoices Ready';
        message = 'Batch invoice generation is complete. You can download the invoices from the Job Status panel.';
        break;
      case 'import':
        title = 'Import Completed';
        const processed = typeof job.processedRecords === 'number' ? job.processedRecords : undefined;
        const total = typeof job.totalRecords === 'number' ? job.totalRecords : undefined;
        let successes = typeof job.successCount === 'number' ? job.successCount : undefined;
        const errors = typeof job.errorCount === 'number' ? job.errorCount : undefined;
        const jobDescriptor = `${job.name ?? ''} ${originalMessage}`.toLowerCase();
        const isModemImport = jobDescriptor.includes('modem');

        if (successes === undefined) {
          const base = processed ?? total;
          if (base !== undefined && errors !== undefined) {
            const diff = base - errors;
            if (Number.isFinite(diff)) {
              successes = diff;
            }
          }
        }

        const processedValue = processed ?? 0;
        const totalValue = total ?? 0;
        const successesValue = successes ?? 0;
        const errorsValue = errors ?? 0;

        if (!isModemImport && successesValue > 0) {
          toastAction = {
            label: 'Reload page',
            icon: 'assets/toast-img/refresh.svg',
            callback: () => {
              window.location.assign('/dashboard');
            },
          };
        }

        const summaryParts: string[] = [];
        summaryParts.push(`Processed ${processedValue}/${totalValue} records`);
        summaryParts.push(`Success: ${successesValue}`);
        summaryParts.push(`Errors: ${errorsValue}`);

        const summary = summaryParts.join(' | ');
        const extraMessage = originalMessage && originalMessage.toLowerCase() !== 'processing...' ? `\n${originalMessage}` : '';
        message = `${summary}${extraMessage}`;
        break;
    }

    const defaultOptions = {
      autohide: false,
      delay: 5000,
    };

    this.toastService.show(
      title,
      message,
      { headerClasses: 'bg-success text-light', ...defaultOptions },
      iconPath,
      undefined,
      undefined,
      undefined,
      toastAction
    );
  }

  private showErrorToast(job: BackgroundJob): void {
    let title = 'Job Failed';
    let message = job.error || 'An error occurred while processing the job.';

    switch (job.type) {
      case 'export':
        title = 'Export Failed';
        break;
      case 'bulk-sync':
        title = 'Sync Failed';
        break;
      case 'batch-invoice':
        title = 'Invoice Generation Failed';
        break;
      case 'import':
        title = 'Import Failed';
        break;
    }

       const defaultOptions = {
            autohide: true,
            delay: 5000,
        };
     this.toastService.show(title, message, { headerClasses: 'bg-danger text-light', ...defaultOptions });
  }

  private restoreActiveJobsFromStorage(): void {
    if (!this.canUseBrowserStorage()) {
      return;
    }

    const storedJobs = localStorage.getItem(this.ACTIVE_JOBS_STORAGE_KEY);
    if (!storedJobs) {
      return;
    }

    try {
      const parsedJobs: any[] = JSON.parse(storedJobs);
      const restoredJobs = new Map<string, BackgroundJob>();
      parsedJobs.forEach(rawJob => {
        const normalized = this.normalizeServerJob(rawJob, rawJob?.apiScope ?? 'client', rawJob?.contextId ?? rawJob?.userID ?? rawJob?.adminID ?? null);
        if (!normalized) {
          return;
        }

        restoredJobs.set(
          normalized.contextKey ?? this.buildContextKey(normalized.id, normalized.contextType ?? normalized.apiScope ?? 'client', normalized.contextId ?? null),
          normalized
        );
      });

      this.activeJobsSubject.next(restoredJobs);
      this.persistActiveJobs();
    } catch (error) {
      console.warn('Failed to restore active jobs from storage', error);
      localStorage.removeItem(this.ACTIVE_JOBS_STORAGE_KEY);
    }
  }

  private persistActiveJobs(): void {
    if (!this.canUseBrowserStorage()) {
      return;
    }

    const jobs = Array.from(this.activeJobsSubject.value.values()).map(job => ({
      ...job,
      startTime: job.startTime instanceof Date ? job.startTime.toISOString() : job.startTime,
      createdAt: job.createdAt instanceof Date ? job.createdAt.toISOString() : job.createdAt,
      completedAt: job.completedAt instanceof Date ? job.completedAt.toISOString() : job.completedAt,
      updatedAt: job.updatedAt instanceof Date ? job.updatedAt.toISOString() : job.updatedAt
    }));

    if (jobs.length === 0) {
      localStorage.removeItem(this.ACTIVE_JOBS_STORAGE_KEY);
      return;
    }

    try {
      localStorage.setItem(this.ACTIVE_JOBS_STORAGE_KEY, JSON.stringify(jobs));
    } catch (error) {
      console.warn('Failed to persist active jobs to storage', error);
    }
  }

  private canUseBrowserStorage(): boolean {
    try {
      return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    } catch (error) {
      return false;
    }
  }

  private coerceNumber(value: any): number | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private normalizeServerJob(
    rawJob: any,
    apiScope: 'client' | 'admin' = 'client',
    userIDOverride?: string | number | null
  ): BackgroundJob | null {
    if (!rawJob) {
      return null;
    }

    const id = rawJob.jobId || rawJob.id;
    if (!id) {
      return null;
    }


    if(rawJob.name == 'Devices Import'){
      rawJob.type = 'devices_import'
    }

    const type = this.normalizeJobType(rawJob.type || rawJob.jobType || rawJob.name);
    const status = this.normalizeJobStatus(rawJob.status);
    const progress = this.normalizeJobProgress(rawJob.progress, status);
    const name = rawJob.name || rawJob.jobName || rawJob.title || rawJob.reportName || this.getDefaultJobName(type);
    const message =
      rawJob.message ||
      rawJob.statusMessage ||
      rawJob.errorMessage ||
      name ||
      'Processing...';

    const rawCreatedAt = rawJob.createdAt 
    const rawUpdatedAt = rawJob.updatedAt
    const createdAt = this.parseDate(rawCreatedAt);
    const startTime = createdAt ?? new Date();
   
    const completedAt = this.parseDate(rawJob.completedAt ?? rawJob.finishedAt);
    const updatedAt = this.parseDate(
      rawUpdatedAt ??
      rawJob.completedAt ??
      rawJob.finishedAt ??
      rawJob.timestamp
    );

    const processedRecords = this.coerceNumber(rawJob.processedRecords);
    const successCount = this.coerceNumber(rawJob.successCount);
    const errorCount = this.coerceNumber(rawJob.errorCount);
    const totalRecords = this.coerceNumber(rawJob.totalRecords);
    const contextId = this.resolveContextId(userIDOverride ?? rawJob?.adminID ?? rawJob?.userID ?? rawJob?.ownerID ?? null, apiScope);
    const contextType = apiScope;

    return {
      id,
      contextKey: this.buildContextKey(String(id), contextType, contextId),
      type,
      status,
      progress,
      message,
      name,
      createdAt: createdAt ?? startTime,
      startTime,
      downloadUrl: rawJob.downloadUrl || rawJob.reportUrl || rawJob.fileUrl,
      error: rawJob.error || rawJob.errorMessage,
      completedAt,
      updatedAt: updatedAt ?? completedAt ?? createdAt ?? startTime,
      format: rawJob.format || rawJob.fileFormat,
      userID: rawJob.userID || rawJob.adminID || rawJob.ownerID || userIDOverride || undefined,
      contextType,
      contextId: contextId ?? undefined,
      apiScope: rawJob.apiScope || apiScope,
      createdAtRaw: rawCreatedAt != null ? String(rawCreatedAt) : null,
      updatedAtRaw: rawUpdatedAt != null ? String(rawUpdatedAt) : null,
      processedRecords,
      successCount,
      errorCount,
      totalRecords
    };
  }

  private normalizeJobType(value: string | undefined): BackgroundJob['type'] {
    const normalized = (value || '').toLowerCase();

    if (normalized.includes('import') && (normalized.includes('modem') || normalized.includes('device') || normalized.includes('meter'))) {
      return 'import';
    }

    switch (normalized) {
      case 'import':
      case 'device_import':
      case 'devices_import':
      case 'device-import':
      case 'devices-import':
      case 'devices import report':
      case 'modem import':
      case 'modems import':
      case 'modems import report':
        return 'import';
      case 'export':
      case 'data-export':
        return 'export';
      case 'bulk-sync':
      case 'bulk_sync':
      case 'sync':
        return 'bulk-sync';
      case 'batch-invoice':
      case 'batch_invoice':
      case 'invoice':
        return 'batch-invoice';
      default:
        return 'export';
    }
  }

  private normalizeJobStatus(value: string | undefined): BackgroundJob['status'] {
    const normalized = (value || '').toLowerCase();

    switch (normalized) {
      case 'pending':
      case 'queued':
        return 'pending';
      case 'processing':
      case 'in-progress':
      case 'in_progress':
      case 'running':
        return 'processing';
      case 'completed':
      case 'complete':
      case 'done':
        return 'completed';
      case 'failed':
      case 'error':
      case 'errored':
        return 'failed';
      default:
        return 'processing';
    }
  }

  private extractJobStatusPayload(response: any): any {
    const data = response?.data ?? response ?? {};

    const candidates = [
      data?.job,
      data?.jobStatus,
      data?.reportJobStatus,
      data?.statusData,
      data?.payload,
      data
    ];

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      if (
        candidate?.status !== undefined ||
        candidate?.progress !== undefined ||
        candidate?.message !== undefined ||
        candidate?.jobId !== undefined ||
        candidate?.id !== undefined
      ) {
        return candidate;
      }
    }

    return data;
  }

  private normalizeJobProgress(progress: any, status: BackgroundJob['status']): number {
    if (typeof progress === 'number' && isFinite(progress)) {
      const clamped = Math.max(0, Math.min(100, progress));
      if (status === 'completed' && clamped < 100) {
        return 100;
      }
      return clamped;
    }

    if (status === 'completed') {
      return 100;
    }

    return 0;
  }

  private parseDate(value: any): Date | undefined {
    if (!value) {
      return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
  }

  private finalizePolling(jobId: string, controller: PollingController): void {
    if (controller.completed) {
      return;
    }

    controller.completed = true;

    if (!controller.stop$.closed) {
      controller.stop$.next();
      controller.stop$.complete();
    }

    if (controller.subscription && !controller.subscription.closed) {
      controller.subscription.unsubscribe();
    }

    this.monitoringSubscriptions.delete(jobId);
  }

  private stopPollingSubscription(jobId: string): void {
    const controller = this.monitoringSubscriptions.get(jobId);
    if (!controller) {
      return;
    }

    controller.stopRequested = true;
    if (controller.pendingRequests === 0) {
      this.finalizePolling(jobId, controller);
    }
  }

  private startGlobalMonitoring(): void {
    this.isMonitoringSubject.next(true);
    
    // Global timer to clean up old notifications and check job health
    this.globalTimerSubscription = timer(0, 60000).pipe( // Every minute
      takeWhile(() => this.isMonitoringSubject.value)
    ).subscribe(() => {
      this.performMaintenance();
    });
  }

  private performMaintenance(): void {
    // Clean up old notifications (older than 24 hours, unless pinned)
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const notifications = this.notificationsSubject.value.filter(n => {
      // Keep pinned notifications and recent ones
      if (n.isPinned) return true;
      if (n.timestamp > oneDayAgo) return true;
      return false;
    });
    
    if (notifications.length !== this.notificationsSubject.value.length) {
      this.notificationsSubject.next(notifications);
    }

    // Check for stuck jobs (jobs that have been processing for too long)
    // const stuckJobs = this.getActiveJobs().filter(job => {
    //   if (job.status === 'processing') {
    //     const processingTime = Date.now() - job.startTime.getTime();
    //     const maxProcessingTime = 120 * 60 * 1000; // 120 minutes
    //     return processingTime > maxProcessingTime;
    //   }
    //   return false;
    // });

    // stuckJobs.forEach(job => {
    //   this.updateJob(job.id, {
    //     status: 'failed',
    //     message: 'Job timed out - processing took too long',
    //     error: 'Job processing exceeded maximum time limit'
    //   });
    // });
  }

  private stopAllMonitoring(): void {
    this.isMonitoringSubject.next(false);
    
    for (const [jobId, controller] of this.monitoringSubscriptions.entries()) {
      controller.stopRequested = true;
      if (controller.pendingRequests === 0) {
        this.finalizePolling(jobId, controller);
      }
    }
  }

  private buildContextKey(jobId: string, contextType: 'client' | 'admin', contextId?: string | number | null): string {
    const resolvedContextId = contextId === null || contextId === undefined || contextId === ''
      ? 'global'
      : String(contextId);
    return `${contextType}:${resolvedContextId}:${jobId}`;
  }

  private buildContextPrefix(contextType: 'client' | 'admin', contextId?: string | number | null): string {
    const resolvedContextId = contextId === null || contextId === undefined || contextId === ''
      ? 'global'
      : String(contextId);
    return `${contextType}:${resolvedContextId}:`;
  }

  private resolveContextId(value: string | number | null | undefined, apiScope: 'client' | 'admin'): string | number | null {
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }

    return apiScope === 'admin' ? localStorage.getItem('adminID') : localStorage.getItem('userID');
  }

  private findJobKey(jobId: string): string | undefined {
    if (this.activeJobsSubject.value.has(jobId)) {
      return jobId;
    }

    for (const [key, job] of this.activeJobsSubject.value.entries()) {
      if (job.id === jobId) {
        return key;
      }
    }

    return undefined;
  }
}
